import { Calendar, Eye, Filter, Hash, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FaEye } from 'react-icons/fa';
import { Button } from '../../app/components';
import { auth } from '../../firebase/config';
import { getReservasByUser } from '../../services/firestoreService';
import { getClientesByUser } from '../../services/userService';
import { Cliente, Reserva } from '../../types';
import { formatNumeroReserva } from '../../utils/reservaUtils';
import '../Reservas/ReservasList.css';
import './ClientesHistorico.css';
import MainHeader from '../Common/MainHeader/MainHeader';
import { DataList, DataListColumn } from '../Common/DataList';
import { Pagination } from '../Common/Pagination';
import ReservaViewModal from '../Reservas/ReservaViewModal';

const ClientesHistorico: React.FC = () => {
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [viewingReserva, setViewingReserva] = useState<Reserva | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const uid = auth.currentUser?.uid;

      if (!uid) {
        setError('Usuário não autenticado');
        return;
      }

      const [reservasResult, clientesResult] = await Promise.all([
        getReservasByUser(uid),
        getClientesByUser()
      ]);

      if (reservasResult.success && reservasResult.data) {
        setReservas(reservasResult.data);
      }
      if (clientesResult.success && clientesResult.data) {
        setClientes(clientesResult.data);
      }
    } catch (error) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (reserva: Reserva) => {
    setViewingReserva(reserva);
    setShowViewModal(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      'reservado': { variant: 'warning', text: 'Reservado' },
      'retirado': { variant: 'info', text: 'Retirado' },
      'devolvido': { variant: 'success', text: 'Devolvido' },
      'cancelado': { variant: 'danger', text: 'Cancelado' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getClienteByReserva = (reserva: Reserva): Cliente | null => {
    return clientes.find(c => c.id === reserva.clienteId) || null;
  };

  const filteredReservas = reservas.filter(reserva => {
    const searchLower = searchTerm.toLowerCase();
 
    const matchesSearch =
      reserva.clienteNome?.toLowerCase().includes(searchLower) ||
      (reserva.produtos && reserva.produtos.some(produto =>
        produto.produtoTitulo.toLowerCase().includes(searchLower)
      )) ||
      (reserva.numeroReserva && reserva.numeroReserva.toLowerCase().includes(searchLower)) ||
      (reserva.numeroReserva && formatNumeroReserva(reserva.numeroReserva).toLowerCase().includes(searchLower));

    const matchesStatus = !statusFilter || reserva.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Lógica de paginação
  const totalPages = Math.ceil(filteredReservas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReservas = filteredReservas.slice(startIndex, endIndex);

  // Funções de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Configuração das colunas do DataList
  const columns: DataListColumn<Reserva>[] = [
    {
      label: "Cliente",
      name: "clienteNome",
      size: 5,
    },
    {
      label: "Nº Reserva",
      size: 2,
      render: (reserva) => (
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold" style={{ fontFamily: 'monospace' }}>
            {formatNumeroReserva(reserva.numeroReserva || '')}
          </span>
        </div>
      ),
    },
    {
      label: "Data Retirada",
      name: "dataRetirada",
      size: 4,
      render: (reserva) => formatDate(reserva.dataRetirada),
    },
    {
      label: "",
      size: 1,
      render: (reserva) => (
        <Button
          btnColor='primary'
          onClick={() => handleView(reserva)}
        >
          <FaEye size={20} />
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid >
      {/* Header com estatísticas */}
      <MainHeader
        title="Histórico de Clientes"
        subtitle="Visualize o histórico completo de reservas por cliente"
        icon={<Calendar size={32} />}
      />

      {/* Card de busca e filtros */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col xs={12} md={4}>
                  <div className="position-relative">
                    <Search size={20} className="position-absolute" style={{ top: '12px', left: '12px', color: '#6c757d' }} />
                    <Hash size={16} className="position-absolute" style={{ top: '14px', right: '12px', color: '#6c757d', opacity: 0.7 }} />
                    <Form.Control
                      type="text"
                      placeholder="Buscar por cliente, produto ou número da reserva..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="ps-5 pe-5"
                      style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={3}>
                  <div className="position-relative mt-3 mt-md-0">
                    <Filter size={20} className="position-absolute" style={{ top: '12px', left: '12px', color: '#6c757d' }} />
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="ps-5"
                      style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                    >
                      <option value="">Todos os status</option>
                      <option value="reservado">Reservado</option>
                      <option value="retirado">Retirado</option>
                      <option value="devolvido">Devolvido</option>
                    </Form.Select>
                  </div>
                </Col>
                <Col xs={12} md={5} className="text-md-end mt-3 mt-md-0">
                  <div className="d-flex flex-column align-items-md-end gap-2">
                    <Badge bg="info" className="px-3 py-2 d-inline-block" style={{ borderRadius: '8px', fontSize: '14px' }}>
                      {filteredReservas.length} resultado{filteredReservas.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Conteúdo principal */}
      <Row>
        <Col>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <DataList
            columns={columns}
            data={paginatedReservas}
            rowKey={(reserva) => reserva.id!}
            disableHoverEffects={true}
            emptyComponent={
              <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <Card.Body className="text-center text-muted py-5">
                  <div className="d-flex flex-column align-items-center gap-3">
                    <Calendar size={48} className="text-muted" />
                    <div>
                      <h5 className="text-muted mb-2">
                        {searchTerm || statusFilter ? 'Nenhuma reserva encontrada' : 'Nenhuma reserva cadastrada'}
                      </h5>
                      <p className="text-muted mb-0">
                        {searchTerm || statusFilter ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira reserva'}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            }
          />

          {filteredReservas.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalItems={filteredReservas.length}
            />
          )}
        </Col>
      </Row>

      <ReservaViewModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewingReserva(null);
        }}
        reserva={viewingReserva}
        cliente={viewingReserva ? getClienteByReserva(viewingReserva) : null}
        onEdit={() => {}}
        onDelete={() => {}}
        onStatusChange={loadData}
      />
    </Container>
  );
};

export default ClientesHistorico;
